import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Select, Typography } from "antd";
import Chart from "react-apexcharts";
import Formatter from "../../Data/Domain/Formatter";
import Loader from "../../Loader/Loader";
import { CustomCard } from "../../Utils/UI/Text";
import { getYearsByUser } from "../../Users/Application/Users.business";
import { getEmissionsByUser } from "../../Emissions/Application/Emissions.business";
import { getYearsListByUserSelector } from "../../Users/Infrastructure/Users.reducer";
import { getEmissionsDataSelector } from "../../Emissions/Infrastructure/Emissions.reducer";
import Storage from "../../Data/Domain/Storage";
import "./LineChart.css";
import User from "../../Users/Domain/User";

const { Text } = Typography;

function LineChart() {
  const yearsList = useSelector(getYearsListByUserSelector);
  const emissionsData = useSelector(getEmissionsDataSelector);

  const [selectedYears, setSelectedYears] = useState([]);
  const [series, setSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const yearColors = {
    2023: "#FF4560",
    2024: "#008FFB",
    2025: "#00E396",
    2026: "#FEB019",
    2027: "#775DD0",
  };

  const labelMonths = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];

  useEffect(() => {
    const userId = new Storage("id").getItem();
    if (userId) {
      getYearsByUser(userId);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        await getEmissionsByUser([]);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (yearsList.length > 0 && selectedYears.length === 0) {
      setSelectedYears(yearsList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearsList]);

  useEffect(() => {
    if (!emissionsData || !emissionsData.years || selectedYears.length === 0) {
      setSeries([]);
      return;
    }

    const newSeries = selectedYears.map((year) => {
      const yearStr = year.toString();
      const emissions = emissionsData.getEmissionsByYear(yearStr) || [];

      const monthlyData = new Array(12).fill(0);

      emissions.forEach((emission) => {
        const date = new Date(emission.date);
        const monthIndex = date.getUTCMonth();
        monthlyData[monthIndex] += emission.tons;
      });

      return {
        name: `Año ${yearStr}`,
        data: monthlyData.map((val) =>
          val > 0 ? Formatter.truncateDecimal(val, 2) : null
        ),
        color: yearColors[year] || "#008FFB",
      };
    });

    setSeries(newSeries);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissionsData, selectedYears]);

  const handleYearChange = (years) => {
    setSelectedYears(years);
    if (years.length > 0) {
      getEmissionsByUser(years);
    }
  };

  const chartOptions = {
    chart: {
      type: "line",
      height: "auto",
      stacked: false,
      zoom: {
        type: "x",
        enabled: true,
        autoScaleYaxis: true,
      },
      toolbar: {
        tools: {
          download: false,
          selection: true,
          zoom: false,
          zoomin: true,
          zoomout: true,
          pan: false,
          reset: false,
        },
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
      },
    },
    colors: series.map((s) => s.color),
    stroke: {
      width: 3,
      curve: "smooth",
    },
    markers: {
      size: 0,
    },
    xaxis: {
      categories: labelMonths,
      title: {
        text: "",
        style: {
          fontWeight: "bold",
          fontFamily: "MontserratBold",
          color: "#041370",
          fontSize: "14px",
        },
      },
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "MontserratRegular",
        },
      },
    },
    yaxis: {
      min: 0,
      title: {
        text: "Tons",
        style: {
          fontWeight: "bold",
          fontFamily: "MontserratBold",
          color: "#041370",
          fontSize: "14px",
        },
      },
      labels: {
        style: {
          fontSize: "12px",
          fontFamily: "Montserrat",
        },
        formatter: function (value) {
          return value ? Formatter.truncateDecimal(value, 2) : "0";
        },
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontSize: "14px",
      fontFamily: "Montserrat",
      markers: {
        width: 12,
        height: 12,
        radius: 2,
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      style: {
        fontSize: "12px",
        fontFamily: "Montserrat",
      },
      y: {
        formatter: function (value) {
          return value
            ? `${Formatter.truncateDecimal(value, 2)} tons`
            : "Sin datos";
        },
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  };
  const role_string = new Storage("rol").getItem();
  const TIMELINE_TITLES = {
    [User.PARTNER]: "Emissions Timeline",
    [User.DEFAULT]: "Removal Timeline",
  };

  return (
    <CustomCard>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          {" "}
          <span className="title-bar-chart">
            {TIMELINE_TITLES[role_string] || "Removal Timeline"}
          </span>
        </div>
        <div style={{ minWidth: "300px", maxWidth: "400px" }}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="Seleccionar años"
            value={selectedYears}
            onChange={handleYearChange}
            options={yearsList.map((year) => ({
              label: year,
              value: year,
            }))}
            maxTagCount={2}
            maxTagPlaceholder={(omittedValues) =>
              `+${omittedValues.length} más`
            }
            className="year-select-custom"
            popupClassName="year-select-dropdown"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="loader-container">
          <Loader />
        </div>
      ) : series.length > 0 ? (
        <div className="chart-container">
          <div className="chart-inner">
            <Chart
              options={chartOptions}
              series={series}
              type="line"
              height="400"
              width="100%"
            />
          </div>
        </div>
      ) : (
        <div className="no-results-container">
          <Text className="no-data-message">
            Selecciona uno o más años para visualizar los datos.
          </Text>
        </div>
      )}
    </CustomCard>
  );
}

export default LineChart;
